// in-client → backend (apps/api) への fetch ラッパ。
// in-client の認証は Zoom Apps SDK のコンテキストから取得した meetingId / userId をヘッダで渡す。
// 本格的なコンテキストトークン検証は後続マイルストーン。

import type {
  ButtonState,
  ButtonType,
  ChatMessage,
  Faq,
  QuickMessage,
  QuickMessageCategory,
  SearchQuery,
  SectionMarker,
  SectionMarkerType,
  Term,
  TermSource,
} from '@solo-webinar-copilot/shared';
import { API_BASE_URL } from '../env.ts';
import type { AppContext } from './zoom-sdk.ts';

export interface SessionState {
  counts: Partial<Record<ButtonType, number>>;
  participantCount: number;
}

export interface PostEventInput {
  type: ButtonType;
  state: ButtonState;
}

function authHeaders(ctx: AppContext): HeadersInit {
  return {
    'x-in-client-meeting': ctx.meetingId,
    'x-in-client-user': ctx.userId,
    'x-in-client-role': ctx.role,
  };
}

async function readErrorMessage(res: Response): Promise<string> {
  try {
    const j = (await res.clone().json()) as { error?: string };
    if (typeof j.error === 'string') return j.error;
  } catch {
    // ignore parse failure
  }
  return `HTTP ${res.status}`;
}

export async function postEvent(
  ctx: AppContext,
  input: PostEventInput,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await fetch(
    `${API_BASE_URL}/in-client/sessions/${encodeURIComponent(ctx.meetingId)}/events`,
    {
      method: 'POST',
      headers: { ...authHeaders(ctx), 'content-type': 'application/json' },
      body: JSON.stringify(input),
    },
  );
  if (!res.ok) {
    return { ok: false, error: await readErrorMessage(res) };
  }
  return { ok: true };
}

export async function fetchState(ctx: AppContext): Promise<SessionState> {
  const res = await fetch(
    `${API_BASE_URL}/in-client/sessions/${encodeURIComponent(ctx.meetingId)}/state`,
    { headers: authHeaders(ctx) },
  );
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return (await res.json()) as SessionState;
}

export async function postReset(
  ctx: AppContext,
  buttonTypes?: readonly ButtonType[],
): Promise<{ ok: true; resetCount: number } | { ok: false; error: string }> {
  const init: RequestInit = {
    method: 'POST',
    headers: { ...authHeaders(ctx), 'content-type': 'application/json' },
  };
  if (buttonTypes) init.body = JSON.stringify({ buttonTypes });
  const res = await fetch(
    `${API_BASE_URL}/in-client/sessions/${encodeURIComponent(ctx.meetingId)}/reset`,
    init,
  );
  if (!res.ok) {
    return { ok: false, error: await readErrorMessage(res) };
  }
  const j = (await res.json()) as { ok: boolean; resetCount: number };
  return { ok: true, resetCount: j.resetCount };
}

// ===== quick_messages =====

export interface QuickMessageDraft {
  label: string;
  content: string;
  category: QuickMessageCategory;
}

export async function listQuickMessages(
  ctx: AppContext,
): Promise<{ ok: true; items: QuickMessage[] } | { ok: false; error: string }> {
  const res = await fetch(`${API_BASE_URL}/in-client/quick-messages`, {
    headers: authHeaders(ctx),
  });
  if (!res.ok) return { ok: false, error: await readErrorMessage(res) };
  const j = (await res.json()) as { items: QuickMessage[] };
  return { ok: true, items: j.items };
}

export async function createQuickMessage(
  ctx: AppContext,
  draft: QuickMessageDraft,
): Promise<{ ok: true; item: QuickMessage } | { ok: false; error: string }> {
  const res = await fetch(`${API_BASE_URL}/in-client/quick-messages`, {
    method: 'POST',
    headers: { ...authHeaders(ctx), 'content-type': 'application/json' },
    body: JSON.stringify(draft),
  });
  if (!res.ok) return { ok: false, error: await readErrorMessage(res) };
  const j = (await res.json()) as { item: QuickMessage };
  return { ok: true, item: j.item };
}

export async function deleteQuickMessage(
  ctx: AppContext,
  id: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await fetch(
    `${API_BASE_URL}/in-client/quick-messages/${encodeURIComponent(id)}`,
    {
      method: 'DELETE',
      headers: authHeaders(ctx),
    },
  );
  if (!res.ok) return { ok: false, error: await readErrorMessage(res) };
  return { ok: true };
}

export async function postQuickMessage(
  ctx: AppContext,
  quickMessageId: string,
): Promise<{ ok: true; content: string } | { ok: false; error: string }> {
  const res = await fetch(
    `${API_BASE_URL}/in-client/sessions/${encodeURIComponent(ctx.meetingId)}/post-quick-message`,
    {
      method: 'POST',
      headers: { ...authHeaders(ctx), 'content-type': 'application/json' },
      body: JSON.stringify({ quickMessageId }),
    },
  );
  if (!res.ok) return { ok: false, error: await readErrorMessage(res) };
  const j = (await res.json()) as { ok: boolean; content: string };
  return { ok: true, content: j.content };
}

// ===== terms =====

export interface TermDraft {
  term: string;
  description: string;
  source: TermSource;
}

export interface ExtractedTermCandidate {
  term: string;
  description: string;
}

export async function listTerms(
  ctx: AppContext,
): Promise<{ ok: true; items: Term[] } | { ok: false; error: string }> {
  const res = await fetch(`${API_BASE_URL}/in-client/terms`, {
    headers: authHeaders(ctx),
  });
  if (!res.ok) return { ok: false, error: await readErrorMessage(res) };
  const j = (await res.json()) as { items: Term[] };
  return { ok: true, items: j.items };
}

export async function createTerm(
  ctx: AppContext,
  draft: TermDraft,
): Promise<{ ok: true; item: Term } | { ok: false; error: string }> {
  const res = await fetch(`${API_BASE_URL}/in-client/terms`, {
    method: 'POST',
    headers: { ...authHeaders(ctx), 'content-type': 'application/json' },
    body: JSON.stringify(draft),
  });
  if (!res.ok) return { ok: false, error: await readErrorMessage(res) };
  const j = (await res.json()) as { item: Term };
  return { ok: true, item: j.item };
}

export async function deleteTerm(
  ctx: AppContext,
  id: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await fetch(
    `${API_BASE_URL}/in-client/terms/${encodeURIComponent(id)}`,
    { method: 'DELETE', headers: authHeaders(ctx) },
  );
  if (!res.ok) return { ok: false, error: await readErrorMessage(res) };
  return { ok: true };
}

export async function describeTermWithAi(
  ctx: AppContext,
  term: string,
  context?: string,
): Promise<
  { ok: true; description: string } | { ok: false; error: string }
> {
  const body: { term: string; context?: string } = { term };
  if (context) body.context = context;
  const res = await fetch(`${API_BASE_URL}/in-client/terms/describe`, {
    method: 'POST',
    headers: { ...authHeaders(ctx), 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) return { ok: false, error: await readErrorMessage(res) };
  const j = (await res.json()) as { description: string };
  return { ok: true, description: j.description };
}

export async function extractTermsFromPdf(
  ctx: AppContext,
  file: File,
): Promise<
  | { ok: true; candidates: ExtractedTermCandidate[] }
  | { ok: false; error: string }
> {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${API_BASE_URL}/in-client/terms/extract-pdf`, {
    method: 'POST',
    headers: authHeaders(ctx),
    body: form,
  });
  if (!res.ok) return { ok: false, error: await readErrorMessage(res) };
  const j = (await res.json()) as { candidates: ExtractedTermCandidate[] };
  return { ok: true, candidates: j.candidates };
}

export async function postTerm(
  ctx: AppContext,
  termId: string,
): Promise<{ ok: true; content: string } | { ok: false; error: string }> {
  const res = await fetch(
    `${API_BASE_URL}/in-client/sessions/${encodeURIComponent(ctx.meetingId)}/post-term`,
    {
      method: 'POST',
      headers: { ...authHeaders(ctx), 'content-type': 'application/json' },
      body: JSON.stringify({ termId }),
    },
  );
  if (!res.ok) return { ok: false, error: await readErrorMessage(res) };
  const j = (await res.json()) as { ok: boolean; content: string };
  return { ok: true, content: j.content };
}

// ===== chat-messages (M7 moderator) =====

export async function listChatMessages(
  ctx: AppContext,
): Promise<{ ok: true; items: ChatMessage[] } | { ok: false; error: string }> {
  const res = await fetch(
    `${API_BASE_URL}/in-client/sessions/${encodeURIComponent(ctx.meetingId)}/chat-messages`,
    { headers: authHeaders(ctx) },
  );
  if (!res.ok) return { ok: false, error: await readErrorMessage(res) };
  const j = (await res.json()) as { items: ChatMessage[] };
  return { ok: true, items: j.items };
}

export async function suggestChatReply(
  ctx: AppContext,
  chatMessageId: string,
): Promise<{ ok: true; candidate: string } | { ok: false; error: string }> {
  const res = await fetch(
    `${API_BASE_URL}/in-client/sessions/${encodeURIComponent(ctx.meetingId)}/chat-messages/${encodeURIComponent(chatMessageId)}/ai-suggest`,
    { method: 'POST', headers: authHeaders(ctx) },
  );
  if (!res.ok) return { ok: false, error: await readErrorMessage(res) };
  const j = (await res.json()) as { candidate: string };
  return { ok: true, candidate: j.candidate };
}

export async function postChatReply(
  ctx: AppContext,
  chatMessageId: string,
  content: string,
  edited: boolean,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await fetch(
    `${API_BASE_URL}/in-client/sessions/${encodeURIComponent(ctx.meetingId)}/chat-messages/${encodeURIComponent(chatMessageId)}/post`,
    {
      method: 'POST',
      headers: { ...authHeaders(ctx), 'content-type': 'application/json' },
      body: JSON.stringify({ content, edited }),
    },
  );
  if (!res.ok) return { ok: false, error: await readErrorMessage(res) };
  return { ok: true };
}

export async function ignoreChatMessage(
  ctx: AppContext,
  chatMessageId: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await fetch(
    `${API_BASE_URL}/in-client/sessions/${encodeURIComponent(ctx.meetingId)}/chat-messages/${encodeURIComponent(chatMessageId)}/ignore`,
    { method: 'POST', headers: authHeaders(ctx) },
  );
  if (!res.ok) return { ok: false, error: await readErrorMessage(res) };
  return { ok: true };
}

// ===== FAQs =====

export interface FaqDraft {
  question: string;
  answer: string;
}

export async function listFaqs(
  ctx: AppContext,
): Promise<{ ok: true; items: Faq[] } | { ok: false; error: string }> {
  const res = await fetch(`${API_BASE_URL}/in-client/faqs`, {
    headers: authHeaders(ctx),
  });
  if (!res.ok) return { ok: false, error: await readErrorMessage(res) };
  const j = (await res.json()) as { items: Faq[] };
  return { ok: true, items: j.items };
}

export async function createFaq(
  ctx: AppContext,
  draft: FaqDraft,
): Promise<{ ok: true; item: Faq } | { ok: false; error: string }> {
  const res = await fetch(`${API_BASE_URL}/in-client/faqs`, {
    method: 'POST',
    headers: { ...authHeaders(ctx), 'content-type': 'application/json' },
    body: JSON.stringify(draft),
  });
  if (!res.ok) return { ok: false, error: await readErrorMessage(res) };
  const j = (await res.json()) as { item: Faq };
  return { ok: true, item: j.item };
}

export interface FaqCandidate {
  question: string;
  answer: string;
}

export async function aiSuggestFaqs(
  ctx: AppContext,
  topic: string,
): Promise<
  | { ok: true; candidates: FaqCandidate[] }
  | { ok: false; error: string }
> {
  const res = await fetch(`${API_BASE_URL}/in-client/faqs/ai-suggest`, {
    method: 'POST',
    headers: { ...authHeaders(ctx), 'content-type': 'application/json' },
    body: JSON.stringify({ topic }),
  });
  if (!res.ok) return { ok: false, error: await readErrorMessage(res) };
  const j = (await res.json()) as { candidates: FaqCandidate[] };
  return { ok: true, candidates: j.candidates };
}

export async function deleteFaq(
  ctx: AppContext,
  id: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await fetch(
    `${API_BASE_URL}/in-client/faqs/${encodeURIComponent(id)}`,
    { method: 'DELETE', headers: authHeaders(ctx) },
  );
  if (!res.ok) return { ok: false, error: await readErrorMessage(res) };
  return { ok: true };
}

// ===== search (M7) =====

export async function postSearch(
  ctx: AppContext,
  query: string,
  sharedToAll: boolean,
): Promise<
  { ok: true; item: SearchQuery } | { ok: false; error: string }
> {
  const res = await fetch(
    `${API_BASE_URL}/in-client/sessions/${encodeURIComponent(ctx.meetingId)}/search`,
    {
      method: 'POST',
      headers: { ...authHeaders(ctx), 'content-type': 'application/json' },
      body: JSON.stringify({ query, sharedToAll }),
    },
  );
  if (!res.ok) return { ok: false, error: await readErrorMessage(res) };
  const j = (await res.json()) as { item: SearchQuery };
  return { ok: true, item: j.item };
}

export async function fetchSearchLog(
  ctx: AppContext,
): Promise<{ ok: true; items: SearchQuery[] } | { ok: false; error: string }> {
  const res = await fetch(
    `${API_BASE_URL}/in-client/sessions/${encodeURIComponent(ctx.meetingId)}/search-log`,
    { headers: authHeaders(ctx) },
  );
  if (!res.ok) return { ok: false, error: await readErrorMessage(res) };
  const j = (await res.json()) as { items: SearchQuery[] };
  return { ok: true, items: j.items };
}

// ===== section markers (M8) =====

export interface SectionMarkerDraft {
  name: string;
  type: SectionMarkerType;
  plannedDurationSeconds?: number;
}

export async function fetchSectionMarkers(
  ctx: AppContext,
): Promise<
  | { ok: true; items: SectionMarker[]; active: SectionMarker | null }
  | { ok: false; error: string }
> {
  const res = await fetch(
    `${API_BASE_URL}/in-client/sessions/${encodeURIComponent(ctx.meetingId)}/section-markers`,
    { headers: authHeaders(ctx) },
  );
  if (!res.ok) return { ok: false, error: await readErrorMessage(res) };
  const j = (await res.json()) as {
    items: SectionMarker[];
    active: SectionMarker | null;
  };
  return { ok: true, items: j.items, active: j.active };
}

export async function startSectionMarker(
  ctx: AppContext,
  draft: SectionMarkerDraft,
): Promise<{ ok: true; item: SectionMarker } | { ok: false; error: string }> {
  const body: Record<string, unknown> = { name: draft.name, type: draft.type };
  if (draft.plannedDurationSeconds !== undefined) {
    body.plannedDurationSeconds = draft.plannedDurationSeconds;
  }
  const res = await fetch(
    `${API_BASE_URL}/in-client/sessions/${encodeURIComponent(ctx.meetingId)}/section-markers`,
    {
      method: 'POST',
      headers: { ...authHeaders(ctx), 'content-type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) return { ok: false, error: await readErrorMessage(res) };
  const j = (await res.json()) as { item: SectionMarker };
  return { ok: true, item: j.item };
}

export async function endSectionMarker(
  ctx: AppContext,
  id: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await fetch(
    `${API_BASE_URL}/in-client/sessions/${encodeURIComponent(ctx.meetingId)}/section-markers/${encodeURIComponent(id)}/end`,
    { method: 'POST', headers: authHeaders(ctx) },
  );
  if (!res.ok) return { ok: false, error: await readErrorMessage(res) };
  return { ok: true };
}
